import { globalCache } from './useDataCache';
import { getApiBase } from '../utils/apiBase';

// Centralized catalog of artifacts by logical page id
// Route mapping is defined below so both desktop and mobile can share the same ids
const ARTIFACTS_BY_PAGE = {
  // Tier 1 (high priority)
  'ftso-submissions': [
    '/submission_summary.json',
    '/feed_counts.json',
    '/feed_config.json',
    '/ftso_performance_dashboard.json'
  ],
  'fdc-submissions': [
    '/fdc_submission_summary.json',
    // '/fdc_submissions.json' removed; page uses /api/fdc/snapshot + SSE
  ],
  'ftso-summary': [
    '/ftso_performance_dashboard.json',
    '/submission_summary.json',
    '/feed_config.json'
  ],
  'ftsooverview': [
    '/ftso_performance_dashboard.json',
    '/submission_summary.json',
    '/feed_counts.json',
    '/feed_config.json'
  ],
  'nodes': [
    '/node_info.json',
    '/node_heads.json',
    '/external_chain_heights.json',
    '/node_db_info.json'
  ],
  'nodesmobile': [
    '/node_info.json',
    '/node_heads.json',
    '/external_chain_heights.json',
    '/node_db_info.json'
  ],
  'nodesoverview': [
    '/node_info.json',
    '/node_heads.json',
    '/external_chain_heights.json'
  ],

  // Tier 2 (medium priority)
  'validators': [
    '/validator_info.json'
  ],
  'indexers': [
    '/indexer_states.json'
  ],

  // Tier 3 (lower priority / everything else)
  'providers': [
    '/provider_data.json',
    '/feed_config.json'
  ],
  'fast-updates': [
    '/fast_updates.json',
    '/feed_config.json'
  ],
  'wallets': [
    '/address_balances.json'
  ],
  'ftso-performance': [
    // '/submit2prices.json' removed to reduce egress; dashboard already contains hit rates/metrics
  ],
  'runtime-logs': [
    '/api/logs/inventory'
  ],
  'disk-usage': [
    '/api/disk/inventory'
  ]
};

// Route path to page id mapping (desktop + mobile)
const ROUTE_TO_PAGE_ID = {
  '/ftso-submissions': 'ftso-submissions',
  '/fdc-submissions': 'fdc-submissions',
  '/ftso-summary': 'ftso-summary',
  '/ftso-performance': 'ftso-performance',
  '/nodes': 'nodes',
  '/indexers': 'indexers',
  '/validators': 'validators',
  '/wallets': 'wallets',
  '/fast-updates': 'fast-updates',
  '/runtime-logs': 'runtime-logs',
  '/disk-usage': 'disk-usage'
};

// Tier assignments (as requested)
const TIERS = {
  1: ['ftso-submissions', 'fdc-submissions', 'ftso-summary', 'ftsooverview', 'nodes', 'nodesmobile', 'nodesoverview'],
  2: ['validators', 'indexers', 'runtime-logs', 'disk-usage'],
  3: Object.keys(ARTIFACTS_BY_PAGE).filter(id => !['ftso-submissions','fdc-submissions','ftso-summary','ftsooverview','nodes','nodesmobile','nodesoverview','validators','indexers','runtime-logs','disk-usage'].includes(id))
};

class GlobalCacheManager {
  constructor() {
    this.endpointRegistry = new Map();
    this.lastTimestamps = {}; // filename -> ISO string
    this._backgroundTimer = null;
    this._isRefreshing = false; // signal to consumers if a refresh is in-flight
  }

  // Expose refresh state to consumers to coordinate UI stabilization
  isRefreshing() {
    return !!this._isRefreshing;
  }

  registerEndpoints(pageId, endpoints) {
    this.endpointRegistry.set(pageId, endpoints);
    console.log(`Registered endpoints for ${pageId}:`, endpoints);
  }

  unregisterEndpoints(pageId) {
    this.endpointRegistry.delete(pageId);
    console.log(`Unregistered endpoints for ${pageId}`);
  }

  async handleSSEUpdate(eventData) {
    console.log('SSE Update received:', eventData);
    try {
      await this.updateByChangedArtifactsWithTiers();
    } catch (e) {
      console.warn('Selective update failed, falling back to refresh all registered endpoints:', e);
      // Update all registered endpoints
      for (const endpoints of this.endpointRegistry.values()) {
        await this.updateEndpoints(endpoints);
      }
      return;
    }
    // Do not redundantly refetch all registered endpoints; changed artifacts will be selectively updated.
  }

  async updateEndpoints(endpoints) {
    const API = getApiBase();
    const fetchPromises = endpoints.map(async (url) => {
      try {
        const fullUrl = /^https?:\/\//i.test(url)
          ? url
          : `${API}${url.startsWith('/') ? '' : '/'}${url}`;
        // Let the browser handle conditional requests (ETag/Last-Modified) to enable 304 responses
        const response = await fetch(fullUrl);
        
        if (!response.ok) {
          console.error(`HTTP error for ${url}: ${response.status} ${response.statusText}`);
          return;
        }
        
        // Add robust JSON parsing with detailed error logging
        let data;
        let textResponse;
        try {
          textResponse = await response.text();
          if (!textResponse.trim()) {
            console.warn(`Empty response for ${url}, skipping cache update`);
            return;
          }
          data = JSON.parse(textResponse);
        } catch (parseError) {
          console.error(`JSON parse error for ${url}:`, parseError);
          console.error(`Response text preview (first 200 chars):`, textResponse ? textResponse.substring(0, 200) : 'N/A');
          console.error(`Full URL was: ${fullUrl}`);
          return; // Skip this endpoint instead of crashing
        }
        
        // Store under the logical key (original URL string)
        globalCache.set(url, data);
        console.log(`Cache updated for ${url} -> ${fullUrl}`);
      } catch (error) {
        console.error(`Failed to update cache for ${url}:`, error);
      }
    });

    await Promise.all(fetchPromises);
  }

  // Fetch export_timestamps.json and update only endpoints whose underlying artifact changed,
  // honoring tier priorities (1 -> 2 -> 3)
  async updateByChangedArtifactsWithTiers() {
    const API = getApiBase();
    const tsUrl = `${API}/export_timestamps.json`;
    // Allow conditional requests (no explicit cache mode); proxies can return 304 when unchanged
    const resp = await fetch(tsUrl);
    if (!resp.ok) throw new Error(`Failed to load export_timestamps.json: ${resp.status}`);
    
    // Add robust JSON parsing with error handling
    let tsJson;
    let textResponse;
    try {
      textResponse = await resp.text();
      if (!textResponse.trim()) {
        console.warn('export_timestamps.json is empty, skipping update');
        return;
      }
      tsJson = JSON.parse(textResponse);
    } catch (parseError) {
      console.error('Failed to parse export_timestamps.json:', parseError);
      console.error('Response text length:', textResponse ? textResponse.length : 'N/A');
      console.error('Response text preview (first 500 chars):', textResponse ? textResponse.substring(0, 500) : 'N/A');
      throw new Error(`JSON parse error in export_timestamps.json: ${parseError.message}`);
    }

    // Compute changed artifact names
    const changed = new Set();
    Object.entries(tsJson || {}).forEach(([fname, meta]) => {
      const last = this.lastTimestamps[fname];
      const cur = meta && meta.last_updated;
      if (!last || (cur && cur !== last)) {
        changed.add(fname);
      }
    });
    // Also detect deletions (rare) — treat as changed
    Object.keys(this.lastTimestamps).forEach((fname) => {
      if (!(fname in (tsJson || {}))) changed.add(fname);
    });

    // Save snapshot
    this.lastTimestamps = {};
    Object.entries(tsJson || {}).forEach(([fname, meta]) => {
      this.lastTimestamps[fname] = meta && meta.last_updated;
    });

    if (changed.size === 0) {
      console.log('No changed artifacts detected from export_timestamps.json');
      return;
    }

    // Helper to collect endpoints by tier impacted by changes
    const collectImpacted = (pageIds) => {
      const set = new Set();
      for (const pid of pageIds) {
        const eps = ARTIFACTS_BY_PAGE[pid] || [];
        for (const ep of eps) {
          for (const fname of changed) {
            if (ep.endsWith(fname)) set.add(ep);
          }
        }
      }
      return Array.from(set);
    };

    const tier1Endpoints = collectImpacted(TIERS[1]);
    const tier2Endpoints = collectImpacted(TIERS[2]);
    const tier3Endpoints = collectImpacted(TIERS[3]);

    // Always include currently registered endpoints (active page) for safety
    const registered = new Set();
    for (const endpoints of this.endpointRegistry.values()) {
      for (const ep of endpoints) {
        for (const fname of changed) {
          if (ep.endsWith(fname)) registered.add(ep);
        }
      }
    }

    // Merge with priority: registered -> tier1 -> tier2 -> tier3
    const merged = Array.from(new Set([
      ...Array.from(registered),
      ...tier1Endpoints,
      ...tier2Endpoints,
      ...tier3Endpoints
    ]));

    if (merged.length === 0) {
      console.log('No endpoints matched changed artifacts across tiers; skipping selective prefetch');
      return;
    }
    try {
      this._isRefreshing = true;
      await this.updateEndpoints(merged);
    } finally {
      this._isRefreshing = false;
    }
  }

  // Prewarm a set of endpoints immediately
  async prewarm(endpoints) {
    await this.updateEndpoints(endpoints);
  }

  // Prewarm by page id using the catalog
  async prewarmForPageId(pageId) {
    const endpoints = ARTIFACTS_BY_PAGE[pageId] || [];
    if (endpoints.length > 0) await this.updateEndpoints(endpoints);
  }

  // Prewarm by route path (e.g., '/ftso-submissions')
  async prewarmForRoute(routePath) {
    const pageId = ROUTE_TO_PAGE_ID[routePath];
    if (pageId) return this.prewarmForPageId(pageId);
  }

  // Start periodic background refresh that checks export_timestamps.json and warms changed artifacts by tiers
  startBackgroundRefresh(intervalMs = 60000) {
    if (this._backgroundTimer) return; // already running
    this._backgroundTimer = setInterval(() => {
      this.updateByChangedArtifactsWithTiers().catch(() => {});
    }, intervalMs);
    console.log(`Background refresh started (interval=${intervalMs}ms)`);
  }

  stopBackgroundRefresh() {
    if (this._backgroundTimer) {
      clearInterval(this._backgroundTimer);
      this._backgroundTimer = null;
      console.log('Background refresh stopped');
    }
  }
}

export const cacheManager = new GlobalCacheManager();